/* ============================================================
   SAM-1186 — Activity (Bookings | Passes | Transactions): pure helpers, no React.
   Time pills, the Date range, and the one Status each view shows (canvas v214).
   ============================================================ */
import { BOOKING_STATUS_CANCELLED, BOOKING_STATUS_COMPLETED, BOOKING_STATUS_CONFIRMED, BOOKING_STATUS_NO_SHOW, BOOKING_STATUS_PENDING } from '../../hooks/useBookings';
import type { Booking } from '../../hooks/useBookings';
import { paymentChipOf, type PaymentChip } from '../../utils/paymentUtils';
import { toDayParam } from '../../api/businessAccessCombined';
import type { Payment } from '../../types/payment.types';
import type { Tone } from './activity';

export type ActivityView = 'bookings' | 'passes' | 'transactions';
/** Bookings and Passes: relative to today. */
export type VisitPill = 'today' | 'upcoming' | 'past';
/** Transactions: a window that ends today. */
export type TxPill = 'today' | '7d' | 'month';

export interface DayRange { from?: string; to?: string }
export type RangePreset = '7d' | '30d' | 'month' | 'last-month';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseDay = (day: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** «Oct 1, 2026» for a YYYY-MM-DD day (the month is always written out). */
export const formatDay = (day: string | undefined): string => {
  const d = day ? parseDay(day) : null;
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : '';
};

/** «Oct 1 – Oct 27, 2026», or «Date range» when none is set. */
export function rangeLabel(range: DayRange): string {
  const a = range.from ? parseDay(range.from) : null;
  const b = range.to ? parseDay(range.to) : null;
  if (a && b) {
    const left = a.getFullYear() === b.getFullYear() ? `${MONTHS[a.getMonth()]} ${a.getDate()}` : formatDay(range.from);
    return `${left} – ${formatDay(range.to)}`;
  }
  if (a) return `From ${formatDay(range.from)}`;
  if (b) return `Until ${formatDay(range.to)}`;
  return 'Date range';
}

export const hasRange = (r: DayRange): boolean => !!(r.from || r.to);

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => { const x = startOfDay(d); x.setDate(x.getDate() + n); return x; };

export function presetRange(preset: RangePreset, now: Date = new Date()): DayRange {
  const today = startOfDay(now);
  if (preset === '7d') return { from: toDayParam(addDays(today, -6)), to: toDayParam(today) };
  if (preset === '30d') return { from: toDayParam(addDays(today, -29)), to: toDayParam(today) };
  if (preset === 'month') return { from: toDayParam(new Date(today.getFullYear(), today.getMonth(), 1)), to: toDayParam(today) };
  return {
    from: toDayParam(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
    to: toDayParam(new Date(today.getFullYear(), today.getMonth(), 0)),
  };
}

/** A range with From after To is swapped, so the picker never produces an empty window. */
export function normalizeRange(r: DayRange): DayRange {
  return r.from && r.to && r.from > r.to ? { from: r.to, to: r.from } : r;
}

/** The /combined window for a visit pill (today / from tomorrow / up to yesterday). */
export function visitPillWindow(pill: VisitPill, now: Date = new Date()): DayRange {
  if (pill === 'today') return { from: toDayParam(now), to: toDayParam(now) };
  if (pill === 'upcoming') return { from: toDayParam(addDays(now, 1)) };
  return { to: toDayParam(addDays(now, -1)) };
}

export function txPillWindow(pill: TxPill, now: Date = new Date()): DayRange {
  if (pill === 'today') return { from: toDayParam(now), to: toDayParam(now) };
  return presetRange(pill === '7d' ? '7d' : 'month', now);
}

/** True when the ISO date-time falls inside the range (local days, both ends included). */
export function inDayRange(iso: string | null | undefined, range: DayRange): boolean {
  if (!hasRange(range)) return true;
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return false;
  const day = toDayParam(d);
  return (!range.from || day >= range.from) && (!range.to || day <= range.to);
}

/* ---------------- Bookings: one Status column ---------------- */

export type BookingStatusKey = 'confirmed' | 'awaiting' | 'cancelled' | 'no-show' | 'completed' | 'pending';

export interface BookingStatusView {
  key: BookingStatusKey;
  label: string;
  tone: Tone;
  /** «Refunded», shown under Cancelled. */
  note?: string;
}

const isRefunded = (s: string | null | undefined) => s === 'refunded' || s === 'partially_refunded';

/**
 * The one status a booking shows: Confirmed, Awaiting payment (a pending booking with an open payment link, which
 * `GET /bookings` annotates as `payment_link_status`; `/crud/booking` does not carry it), Cancelled (with «Refunded» under it when the money went back), No-show or Completed.
 */
export function bookingStatusView(b: Pick<Booking, 'status_id' | 'payment_status' | 'payment_link_status'>): BookingStatusView {
  const id = Number(b.status_id);
  if (id === BOOKING_STATUS_CANCELLED) {
    return { key: 'cancelled', label: 'Cancelled', tone: 'bad', ...(isRefunded(b.payment_status) ? { note: 'Refunded' } : {}) };
  }
  if (id === BOOKING_STATUS_NO_SHOW) return { key: 'no-show', label: 'No-show', tone: 'bad' };
  if (id === BOOKING_STATUS_COMPLETED) return { key: 'completed', label: 'Completed', tone: 'off' };
  if (id === BOOKING_STATUS_PENDING) {
    // Only an open payment link is «Awaiting payment»: a card hold is also pending and is not waiting for the guest.
    return b.payment_link_status === 'pending_payment'
      ? { key: 'awaiting', label: 'Awaiting payment', tone: 'wait' }
      : { key: 'pending', label: 'Pending', tone: 'wait' };
  }
  if (id === BOOKING_STATUS_CONFIRMED) return { key: 'confirmed', label: 'Confirmed', tone: 'ok' };
  return { key: 'pending', label: 'Pending', tone: 'off' };
}

export const BOOKING_STATUS_FILTERS: Array<{ k: 'all' | BookingStatusKey; label: string }> = [
  { k: 'all', label: 'All' },
  { k: 'confirmed', label: 'Confirmed' },
  { k: 'awaiting', label: 'Awaiting payment' },
  { k: 'cancelled', label: 'Cancelled' },
  { k: 'no-show', label: 'No-show' },
];

/** Bookings visible for a pill + range + status + search (client side: /booking returns the whole business). */
export function filterBookings(
  bookings: Booking[],
  opts: { pill: VisitPill; range: DayRange; status: 'all' | BookingStatusKey; query: string; now?: Date },
): Booking[] {
  const now = opts.now ?? new Date();
  const window = hasRange(opts.range) ? opts.range : visitPillWindow(opts.pill, now);
  const q = opts.query.trim().toLowerCase();
  return bookings.filter((b) => {
    if (!b.reservation_start) return false;
    if (!inDayRange(b.reservation_start, window)) return false;
    if (opts.status !== 'all' && bookingStatusView(b).key !== opts.status) return false;
    if (q && !`${b.user_name ?? ''} ${b.user_email ?? ''} ${b.service_name ?? ''} ${b.confirmation_code ?? ''}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

/** «AB» from a name, for the guest avatar. */
export function initials(name: string | null | undefined): string {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/* ---------------- Transactions ---------------- */

export type TxStatusFilter = 'all' | PaymentChip;

export const TX_STATUS_FILTERS: Array<{ k: TxStatusFilter; label: string }> = [
  { k: 'all', label: 'All' },
  { k: 'paid', label: 'Paid' },
  { k: 'failed', label: 'Declined' },
  { k: 'voided', label: 'Voided' },
  { k: 'refunded', label: 'Refunded' },
  { k: 'partiallyRefunded', label: 'Partially refunded' },
];

/** «Declined» is how a failed charge reads on screen. */
export const TX_CHIP_LABEL: Record<PaymentChip, string> = {
  paid: 'Paid', voided: 'Voided', refunded: 'Refunded', partiallyRefunded: 'Partially refunded', failed: 'Declined',
};

export const TX_CHIP_TONE: Record<PaymentChip, Tone> = {
  paid: 'ok', voided: 'off', refunded: 'off', partiallyRefunded: 'wait', failed: 'bad',
};

export function filterTransactions(
  payments: Payment[],
  opts: { range: DayRange; status: TxStatusFilter; query: string; searchText: (p: Payment) => string },
): Payment[] {
  const q = opts.query.trim().toLowerCase();
  return payments
    .filter((p) => inDayRange(p.payment_date || p.createdAt, opts.range))
    .filter((p) => opts.status === 'all' || paymentChipOf(p) === opts.status)
    .filter((p) => !q || opts.searchText(p).toLowerCase().includes(q))
    .sort((a, b) => new Date(b.payment_date || b.createdAt || 0).getTime() - new Date(a.payment_date || a.createdAt || 0).getTime());
}

/** «11:00 AM» for today, «Oct 28, 2026 · 10:00 AM» otherwise (the month is always written out). */
export function formatVisit(iso: string | null | undefined, now: Date = new Date()): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (toDayParam(d) === toDayParam(now)) return time;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${time}`;
}
